/**
 * ========================
 * COMMUNICATIONS · PUSH MENU PROVIDER
 * ========================
 *
 * Menu for the Push channel sub-app (`/admin/communications/push/*`).
 * `comms-push` is a `hidden` domain reached from the Communications hub.
 * Visibility is permission-driven (Communications.Push.*); Admins bypass.
 */

import {
  IconLayoutDashboard,
  IconPencil,
  IconUsersGroup,
  IconSend,
  IconTemplate,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { PERMISSIONS } from '../../config/permissions';

const P = PERMISSIONS.COMMUNICATIONS.PUSH;
const BASE = '/admin/communications/push';

class CommsPushMenuProvider implements DomainMenuProvider {
  domainId = 'comms-push';

  getMenuConfig(): DomainMenuConfig {
    const view = createVisibilityCheck({ permissions: [P.VIEW] });
    const send = createVisibilityCheck({ permissions: [P.SEND] });
    const manage = createVisibilityCheck({ permissions: [P.MANAGE] });
    const settings = createVisibilityCheck({ permissions: [P.SETTINGS] });
    const campaigns = createVisibilityCheck({ permissions: [PERMISSIONS.COMMUNICATIONS.CAMPAIGNS.VIEW] });

    const sections: MenuSection[] = [
      {
        id: 'comms-push-overview',
        label: 'Overview',
        visible: view,
        items: [
          { id: 'comms-push-dashboard', label: 'Dashboard', path: BASE, icon: <IconLayoutDashboard size={16} />, visible: view },
        ],
      },
      {
        id: 'comms-push-send',
        label: 'Send',
        visible: send,
        items: [
          { id: 'comms-push-compose', label: 'Compose', path: `${BASE}/compose`, icon: <IconPencil size={16} />, visible: send },
          { id: 'comms-push-bulk', label: 'Bulk Send', path: `${BASE}/bulk`, icon: <IconUsersGroup size={16} />, visible: send },
        ],
      },
      {
        id: 'comms-push-messages',
        label: 'Messages',
        visible: view,
        items: [
          { id: 'comms-push-outbox', label: 'Outbox', path: `${BASE}/outbox`, icon: <IconSend size={16} />, visible: view },
          { id: 'comms-push-campaigns', label: 'Campaigns', path: `${BASE}/campaigns`, icon: <IconSpeakerphone size={16} />, visible: campaigns },
        ],
      },
      {
        id: 'comms-push-library',
        label: 'Library',
        visible: manage,
        items: [
          { id: 'comms-push-templates', label: 'Templates', path: `${BASE}/templates`, icon: <IconTemplate size={16} />, visible: manage },
        ],
      },
      {
        id: 'comms-push-config',
        label: 'Configuration',
        visible: settings,
        items: [
          { id: 'comms-push-settings', label: 'Settings', path: `${BASE}/settings`, icon: <IconSettings size={16} />, visible: settings },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: { version: '1.0.0', lastUpdated: Date.now(), description: 'Push channel — admin console' },
    };
  }
}

export const commsPushMenuProvider = new CommsPushMenuProvider();
